import { useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  Divider,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import CalculateIcon from '@mui/icons-material/Calculate'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SavingsIcon from '@mui/icons-material/Savings'
import ScheduleIcon from '@mui/icons-material/Schedule'
import Co2Icon from '@mui/icons-material/Co2'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import SettingsIcon from '@mui/icons-material/Settings'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import TimelineIcon from '@mui/icons-material/Timeline'
import ScienceIcon from '@mui/icons-material/Science'
import InfoIcon from '@mui/icons-material/Info'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { useComparison } from '../context/ComparisonContext'
import {
  EQUIPMENT_STATS,
  FINANCIAL_STATS
} from '../services/corpusStatistics'
import {
  compareModels,
  calculateLifetimeSavings,
  calculatePreventiveROI,
  comparePreventiveVsRestorative,
  generateProjectionData,
  getExpectedLife,
  getTypicalKwTon,
  AGE_DATA_SAMPLE_SIZE,
  calculateAvoidedDegradationCosts,
  calculateAvoidedDemandCharges,
  calculateAvoidedExtendedRuntimeCosts,
  calculateLifeExtensionValue
} from '../services/degradationCurve'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MetricExplanationModal from '../components/MetricExplanationModal'

// ROI calculation logic based on REAL corpus data
const baseImprovement = {
  Chiller: EQUIPMENT_STATS.Chiller?.mean || 21.9,
  RTU: EQUIPMENT_STATS.RTU?.mean || 31.5,
  CRAC: EQUIPMENT_STATS.CRAC?.mean || 26.1,
  'Split System': EQUIPMENT_STATS['Split System']?.mean || 24.3,
  'DX System': EQUIPMENT_STATS['DX System']?.mean || 31.1,
  Refrigeration: EQUIPMENT_STATS.Refrigeration?.mean || 28.0
}

// Standard deviations from corpus
const baseStdDev = {
  Chiller: EQUIPMENT_STATS.Chiller?.stdDev || 12.7,
  RTU: EQUIPMENT_STATS.RTU?.stdDev || 19.2,
  CRAC: EQUIPMENT_STATS.CRAC?.stdDev || 8.3,
  'Split System': EQUIPMENT_STATS['Split System']?.stdDev || 12.6,
  'DX System': EQUIPMENT_STATS['DX System']?.stdDev || 12.5,
  Refrigeration: EQUIPMENT_STATS.Refrigeration?.stdDev || 11.5
}

// Sample sizes from corpus
const baseSampleSize = {
  Chiller: EQUIPMENT_STATS.Chiller?.n || 65,
  RTU: EQUIPMENT_STATS.RTU?.n || 10,
  CRAC: EQUIPMENT_STATS.CRAC?.n || 13,
  'Split System': EQUIPMENT_STATS['Split System']?.n || 11,
  'DX System': EQUIPMENT_STATS['DX System']?.n || 4,
  Refrigeration: EQUIPMENT_STATS.Refrigeration?.n || 13
}

const ageModifier = (age) => {
  if (age < 5) return 0.85
  if (age < 10) return 1.0
  if (age < 15) return 1.05
  if (age < 20) return 1.02
  return 0.95
}

const operatingHours = {
  '24/7': 8760,
  'Extended': 4000,
  'Commercial': 2500,
  'Seasonal': 1500
}

const DEFAULT_TREATMENT_COST_PER_TON = FINANCIAL_STATS.treatmentCostPerTon.value
const DEFAULT_CO2_FACTOR = 0.42 // EPA average kg CO2/kWh

// Use getTypicalKwTon() from degradationCurve.js for equipment ratings

function ROICalculator() {
  const { addROIScenario, roiScenarios } = useComparison()
  const [showAdvanced, setShowAdvanced] = useState(true)
  const [showLifetime, setShowLifetime] = useState(true)
  const [modalMetric, setModalMetric] = useState(null)
  const [inputMode, setInputMode] = useState('estimate') // 'estimate' or 'specs'
  const [formData, setFormData] = useState({
    equipmentType: 'Chiller',
    capacityTons: 100,
    equipmentAge: 10,
    operatingProfile: 'Commercial',
    utilityRate: 0.12,
    currentKwPerTon: 1.4,
    // Specs mode inputs
    originalKwTon: null, // null = use estimate
    originalEER: null,
    // Advanced parameters
    customHours: null, // null = use profile preset
    treatmentCostPerTon: DEFAULT_TREATMENT_COST_PER_TON,
    co2Factor: DEFAULT_CO2_FACTOR,
    scenarioName: '',
    paymentOption: 'upfront' // 'upfront' or 'deferred' (3-year)
  })

  const [calculated, setCalculated] = useState(false)
  const [savedToCompare, setSavedToCompare] = useState(false)

  // Model comparison (ASHRAE vs Corpus)
  const modelComparison = useMemo(() => {
    return compareModels(formData.equipmentAge)
  }, [formData.equipmentAge])

  // Lifetime savings projection
  const lifetimeProjection = useMemo(() => {
    const hours = formData.customHours || operatingHours[formData.operatingProfile]
    const originalKw = inputMode === 'specs' && formData.originalKwTon
      ? formData.originalKwTon
      : getTypicalKwTon(formData.equipmentType)

    return calculateLifetimeSavings({
      capacityTons: formData.capacityTons,
      currentAgeYears: formData.equipmentAge,
      originalKwTon: originalKw,
      runtimeHoursPerYear: hours,
      electricityRate: formData.utilityRate,
      equipmentType: formData.equipmentType,
      model: 'corpus'
    })
  }, [formData, inputMode])

  // Year-by-year projection data for display
  const projectionData = useMemo(() => {
    const hours = formData.customHours || operatingHours[formData.operatingProfile]
    const originalKw = inputMode === 'specs' && formData.originalKwTon
      ? formData.originalKwTon
      : getTypicalKwTon(formData.equipmentType)

    return generateProjectionData({
      capacityTons: formData.capacityTons,
      currentAgeYears: formData.equipmentAge,
      originalKwTon: originalKw,
      runtimeHoursPerYear: hours,
      electricityRate: formData.utilityRate,
      equipmentType: formData.equipmentType
    })
  }, [formData, inputMode])

  // Preventive vs Restorative ROI comparison
  const roiComparison = useMemo(() => {
    const hours = formData.customHours || operatingHours[formData.operatingProfile]
    const originalKw = inputMode === 'specs' && formData.originalKwTon
      ? formData.originalKwTon
      : getTypicalKwTon(formData.equipmentType)

    return comparePreventiveVsRestorative({
      capacityTons: formData.capacityTons,
      originalKwTon: originalKw,
      runtimeHoursPerYear: hours,
      electricityRate: formData.utilityRate,
      equipmentType: formData.equipmentType,
      currentAgeYears: formData.equipmentAge
    })
  }, [formData, inputMode])

  const results = useMemo(() => {
    const base = baseImprovement[formData.equipmentType] || 23
    const stdDev = baseStdDev[formData.equipmentType] || 12
    const n = baseSampleSize[formData.equipmentType] || 10
    const modifier = ageModifier(formData.equipmentAge)
    const improvementPct = base * modifier

    // Use custom hours if set, otherwise use profile preset
    const hours = formData.customHours || operatingHours[formData.operatingProfile]
    const currentKwh = formData.currentKwPerTon * formData.capacityTons * hours
    const improvedKwh = currentKwh * (1 - improvementPct / 100)
    const annualKwhSaved = currentKwh - improvedKwh

    // Component 1: Direct energy savings
    const annualEnergySavings = annualKwhSaved * formData.utilityRate
    const treatmentCost = formData.capacityTons * formData.treatmentCostPerTon

    // Components 2-5: Avoided costs (5-component corpus-validated model)
    const avoidedDegradation = calculateAvoidedDegradationCosts({
      equipmentType: formData.equipmentType,
      currentAge: formData.equipmentAge,
      tonnage: formData.capacityTons,
      operatingHours: hours,
      electricityRate: formData.utilityRate
    })

    const avoidedDemand = calculateAvoidedDemandCharges({
      equipmentType: formData.equipmentType,
      currentAge: formData.equipmentAge,
      tonnage: formData.capacityTons,
      demandRate: 12 // Default demand rate
    })

    const avoidedRuntime = calculateAvoidedExtendedRuntimeCosts({
      equipmentType: formData.equipmentType,
      currentAge: formData.equipmentAge,
      tonnage: formData.capacityTons,
      operatingHours: hours,
      electricityRate: formData.utilityRate
    })

    const lifeExtension = calculateLifeExtensionValue({
      equipmentType: formData.equipmentType,
      currentAge: formData.equipmentAge,
      tonnage: formData.capacityTons
    })

    // Total annual value (5 corpus-validated components)
    const totalAvoidedCosts =
      avoidedDegradation.annualizedAvoidedCost +
      avoidedDemand.annualizedAvoidedDemand +
      avoidedRuntime.annualizedAvoidedRuntime +
      lifeExtension.annualizedLifeExtensionValue

    const totalAnnualValue = annualEnergySavings + totalAvoidedCosts

    // Payback uses total value (not just energy savings)
    const paybackMonths = totalAnnualValue > 0 ? (treatmentCost / totalAnnualValue) * 12 : null
    const fiveYearROI = treatmentCost > 0 ? ((totalAnnualValue * 5) - treatmentCost) / treatmentCost * 100 : 0
    const co2Avoided = annualKwhSaved * formData.co2Factor / 1000 // metric tonnes

    // Calculate range using actual stdDev from corpus
    const floorImprovement = Math.max(5, improvementPct - stdDev)
    const optimisticImprovement = Math.min(60, improvementPct + stdDev)

    // Calculate payment-specific cash flow metrics (using total annual value)
    const annualPayment = formData.paymentOption === 'deferred'
      ? treatmentCost / 3
      : treatmentCost
    const year1NetCashFlow = formData.paymentOption === 'deferred'
      ? totalAnnualValue - (treatmentCost / 3)
      : totalAnnualValue - treatmentCost
    const cashFlowPositiveYear1 = formData.paymentOption === 'deferred'
      && totalAnnualValue > (treatmentCost / 3)

    // Monthly payment breakdown (36 monthly payments over 3 years)
    const monthlyPayment = formData.paymentOption === 'deferred'
      ? treatmentCost / 36
      : null
    const monthlySavings = totalAnnualValue / 12
    const monthlyNetCashFlow = formData.paymentOption === 'deferred'
      ? monthlySavings - monthlyPayment
      : null
    const cashFlowPositiveMonthly = monthlyNetCashFlow !== null && monthlyNetCashFlow > 0

    return {
      improvementPct: improvementPct.toFixed(1),
      stdDev: stdDev.toFixed(1),
      n,
      improvementRange: {
        low: floorImprovement.toFixed(1),
        high: optimisticImprovement.toFixed(1)
      },
      annualKwhSaved: Math.round(annualKwhSaved),
      // Energy-only savings (for reference)
      annualEnergySavings: Math.round(annualEnergySavings),
      // Legacy field name for compatibility
      annualCostSaved: Math.round(totalAnnualValue),
      // 5-component value breakdown
      totalAnnualValue: Math.round(totalAnnualValue),
      totalAvoidedCosts: Math.round(totalAvoidedCosts),
      valueBreakdown: {
        energySavings: Math.round(annualEnergySavings),
        avoidedDegradation: Math.round(avoidedDegradation.annualizedAvoidedCost),
        avoidedDemand: Math.round(avoidedDemand.annualizedAvoidedDemand),
        avoidedRuntime: Math.round(avoidedRuntime.annualizedAvoidedRuntime),
        lifeExtension: Math.round(lifeExtension.annualizedLifeExtensionValue)
      },
      treatmentCost: Math.round(treatmentCost),
      paybackMonths: paybackMonths !== null ? paybackMonths.toFixed(1) : 'N/A',
      fiveYearROI: fiveYearROI.toFixed(0),
      co2Avoided: co2Avoided.toFixed(1),
      hoursUsed: hours,
      dataSource: `Based on ${n} PE-certified ${formData.equipmentType} measurements`,
      modelVersion: '5-component corpus-validated',
      // Payment option metrics
      annualPayment: Math.round(annualPayment),
      year1NetCashFlow: Math.round(year1NetCashFlow),
      cashFlowPositiveYear1,
      // Monthly breakdown (for 3-year deferred)
      monthlyPayment: monthlyPayment ? Math.round(monthlyPayment) : null,
      monthlySavings: Math.round(monthlySavings),
      monthlyNetCashFlow: monthlyNetCashFlow !== null ? Math.round(monthlyNetCashFlow) : null,
      cashFlowPositiveMonthly
    }
  }, [formData])

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSliderChange = (field) => (_, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setSavedToCompare(false)
  }

  const handleSaveToCompare = () => {
    const name = formData.scenarioName || `${formData.equipmentType} ${formData.capacityTons}T`
    addROIScenario({
      id: `roi-${Date.now()}`,
      name,
      type: 'roi',
      inputs: { ...formData },
      results: { ...results },
      createdAt: new Date().toISOString()
    })
    setSavedToCompare(true)
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        ROI Calculator
      </Typography>

      <Grid container spacing={4}>
        {/* Input Form */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Equipment Details
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Equipment Type</InputLabel>
                <Select
                  value={formData.equipmentType}
                  label="Equipment Type"
                  onChange={handleChange('equipmentType')}
                >
                  {Object.keys(baseImprovement).map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Capacity (Tons)"
                type="number"
                inputProps={{ min: 0 }}
                value={formData.capacityTons}
                onChange={(e) => {
                  const val = Math.max(0, parseFloat(e.target.value) || 0)
                  setFormData(prev => ({ ...prev, capacityTons: val }))
                }}
                sx={{ mb: 3 }}
              />

              <Typography gutterBottom>
                Equipment Age: {formData.equipmentAge} years
              </Typography>
              <Slider
                value={formData.equipmentAge}
                onChange={handleSliderChange('equipmentAge')}
                min={0}
                max={25}
                marks={[
                  { value: 0, label: '0' },
                  { value: 10, label: '10' },
                  { value: 20, label: '20' }
                ]}
                sx={{
                  mb: 3,
                  '& .MuiSlider-thumb': { width: 20, height: 20 }
                }}
              />

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Operating Profile</InputLabel>
                <Select
                  value={formData.operatingProfile}
                  label="Operating Profile"
                  onChange={handleChange('operatingProfile')}
                >
                  <MenuItem value="24/7">24/7 (Data Center, Hospital)</MenuItem>
                  <MenuItem value="Extended">Extended (Retail, Restaurant)</MenuItem>
                  <MenuItem value="Commercial">Commercial (Office, Bank)</MenuItem>
                  <MenuItem value="Seasonal">Seasonal</MenuItem>
                </Select>
              </FormControl>

              {/* Payment Option */}
              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom variant="body2" color="text.secondary">
                  Payment Option
                </Typography>
                <ToggleButtonGroup
                  value={formData.paymentOption}
                  exclusive
                  onChange={(_, value) => value && setFormData(prev => ({ ...prev, paymentOption: value }))}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="upfront">
                    Upfront Payment
                  </ToggleButton>
                  <ToggleButton value="deferred">
                    3-Year Deferred
                  </ToggleButton>
                </ToggleButtonGroup>
                {formData.paymentOption === 'deferred' && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontWeight: 600 }}>
                      36 Monthly Payments
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      ${results.monthlyPayment?.toLocaleString()}/month
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Est. monthly savings: ${results.monthlySavings?.toLocaleString()} → Net:{' '}
                      <span style={{ color: results.cashFlowPositiveMonthly ? '#2e7d32' : 'inherit', fontWeight: 600 }}>
                        {results.monthlyNetCashFlow >= 0 ? '+' : ''}${results.monthlyNetCashFlow?.toLocaleString()}/month
                      </span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                      Subject to credit approval. Other financing options may be available.
                    </Typography>
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                label="Utility Rate ($/kWh)"
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.utilityRate}
                onChange={(e) => {
                  const val = Math.max(0, parseFloat(e.target.value) || 0)
                  setFormData(prev => ({ ...prev, utilityRate: val }))
                }}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Current kW/Ton"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={formData.currentKwPerTon}
                onChange={(e) => {
                  const val = Math.max(0, parseFloat(e.target.value) || 0)
                  setFormData(prev => ({ ...prev, currentKwPerTon: val }))
                }}
                helperText="Industry avg: 1.2-1.6 kW/ton"
                sx={{ mb: 3 }}
              />

              {/* Advanced Parameters Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} onClick={() => setShowAdvanced(!showAdvanced)}>
                <SettingsIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Advanced Parameters
                </Typography>
                <IconButton size="small" sx={{ ml: 'auto' }}>
                  {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={showAdvanced}>
                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 3 }}>
                  <Typography gutterBottom variant="body2">
                    Custom Operating Hours: {formData.customHours || operatingHours[formData.operatingProfile]} hrs/yr
                    {formData.customHours && <Chip label="Custom" size="small" sx={{ ml: 1 }} />}
                  </Typography>
                  <Slider
                    value={formData.customHours || operatingHours[formData.operatingProfile]}
                    onChange={(_, value) => setFormData(prev => ({ ...prev, customHours: value }))}
                    min={500}
                    max={8760}
                    step={100}
                    marks={[
                      { value: 1500, label: '1.5K' },
                      { value: 4000, label: '4K' },
                      { value: 8760, label: '24/7' }
                    ]}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Treatment Cost ($/ton)"
                    type="number"
                    inputProps={{ step: 5 }}
                    value={formData.treatmentCostPerTon}
                    onChange={handleChange('treatmentCostPerTon')}
                    helperText="Default: $125/ton"
                    sx={{ mb: 2 }}
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="CO2 Emission Factor (kg/kWh)"
                    type="number"
                    inputProps={{ step: 0.01 }}
                    value={formData.co2Factor}
                    onChange={handleChange('co2Factor')}
                    helperText="EPA avg: 0.42 kg/kWh. Varies by region."
                    sx={{ mb: 2 }}
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Scenario Name (for comparison)"
                    value={formData.scenarioName}
                    onChange={handleChange('scenarioName')}
                    placeholder={`${formData.equipmentType} ${formData.capacityTons}T`}
                    size="small"
                  />
                </Box>
              </Collapse>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CalculateIcon />}
                onClick={() => setCalculated(true)}
              >
                Calculate ROI
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={7}>
          <Card sx={{ bgcolor: calculated ? 'background.paper' : 'action.hover' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Projected Results
              </Typography>

              {!calculated && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Adjust equipment details and click Calculate to refine projections
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrendingUpIcon color="primary" />
                        <Typography color="text.secondary" variant="body2">
                          Efficiency Improvement
                        </Typography>
                      </Box>
                      <Typography variant="h4" color="primary.main" fontWeight={700}>
                        {results.improvementPct}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Range: {results.improvementRange.low}% - {results.improvementRange.high}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <SavingsIcon color="success" />
                        <Typography color="text.secondary" variant="body2">
                          Total Annual Value
                        </Typography>
                        <IconButton size="small" onClick={() => setModalMetric('total_annual_value')} sx={{ p: 0 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </IconButton>
                      </Box>
                      <Typography variant="h4" color="success.main" fontWeight={700}>
                        ${results.totalAnnualValue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Energy: ${results.valueBreakdown?.energySavings?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        + Avoided costs: ${results.totalAvoidedCosts.toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ScheduleIcon color="warning" />
                        <Typography color="text.secondary" variant="body2">
                          True Payback
                        </Typography>
                        <IconButton size="small" onClick={() => setModalMetric('payback_months')} sx={{ p: 0 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </IconButton>
                      </Box>
                      <Typography variant="h4" color="warning.main" fontWeight={700}>
                        {results.paybackMonths === 'N/A' ? 'N/A' : `${results.paybackMonths} mo`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {results.paybackMonths === 'N/A' ? 'Enter utility rate to calculate' : `Treatment cost: $${results.treatmentCost.toLocaleString()}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Co2Icon color="info" />
                        <Typography color="text.secondary" variant="body2">
                          CO2 Avoided (Annual)
                        </Typography>
                      </Box>
                      <Typography variant="h4" color="info.main" fontWeight={700}>
                        {results.co2Avoided} tonnes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        5-Year ROI: {results.fiveYearROI}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Year 1 Net Cash Flow Card */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      bgcolor: (theme) => results.cashFlowPositiveYear1
                        ? (theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)')
                        : 'inherit',
                      borderColor: results.cashFlowPositiveYear1 ? 'success.main' : 'divider',
                      borderWidth: results.cashFlowPositiveYear1 ? 2 : 1
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <AccountBalanceIcon color={results.year1NetCashFlow >= 0 ? 'success' : 'warning'} />
                        <Typography color="text.secondary" variant="body2">
                          {formData.paymentOption === 'deferred' ? 'Cash Flow Summary' : 'Year 1 Net Cash Flow'}
                        </Typography>
                        {results.cashFlowPositiveYear1 && (
                          <Chip label="Cash Flow Positive" size="small" color="success" />
                        )}
                        <Chip
                          label={formData.paymentOption === 'deferred' ? '36 Monthly Payments' : 'Upfront'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      {formData.paymentOption === 'deferred' ? (
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Month 1 Net
                            </Typography>
                            <Typography
                              variant="h5"
                              color={results.monthlyNetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                              fontWeight={700}
                            >
                              {results.monthlyNetCashFlow >= 0 ? '+' : ''}${results.monthlyNetCashFlow?.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Year 1 Total
                            </Typography>
                            <Typography
                              variant="h5"
                              color={results.year1NetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                              fontWeight={700}
                            >
                              {results.year1NetCashFlow >= 0 ? '+' : ''}${results.year1NetCashFlow.toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography
                          variant="h4"
                          color={results.year1NetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                          fontWeight={700}
                        >
                          ${results.year1NetCashFlow.toLocaleString()}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {formData.paymentOption === 'deferred'
                          ? `Monthly: $${results.monthlySavings?.toLocaleString()} savings − $${results.monthlyPayment?.toLocaleString()} payment`
                          : `Savings $${results.annualCostSaved.toLocaleString()} − Treatment $${results.treatmentCost.toLocaleString()}`
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Cost of Doing Nothing Card */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      bgcolor: (theme) => theme.palette.mode === 'light'
                        ? 'rgba(211, 47, 47, 0.06)'
                        : 'rgba(211, 47, 47, 0.12)',
                      borderColor: 'error.main',
                      borderWidth: 2
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <WarningAmberIcon color="error" />
                        <Typography variant="subtitle1" color="error.main" fontWeight={700}>
                          Cost of Doing Nothing
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            This Year's Waste
                          </Typography>
                          <Typography variant="h5" color="error.main" fontWeight={700}>
                            ${(projectionData.projectionYears[0]?.annualSavings || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            5-Year Cumulative Waste
                          </Typography>
                          <Typography variant="h5" color="error.main" fontWeight={700}>
                            ${(projectionData.projectionYears[4]?.cumulativeSavings || projectionData.totalLifetimeSavings || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Lifetime Waste ({lifetimeProjection.lifetime.remainingLifeYears} yrs)
                          </Typography>
                          <Typography variant="h5" color="error.main" fontWeight={700}>
                            ${(projectionData.totalLifetimeSavings || 0).toLocaleString()}
                          </Typography>
                        </Grid>
                      </Grid>

                      {projectionData.projectionYears.length >= 5 &&
                        projectionData.projectionYears[4].annualSavings > projectionData.projectionYears[0].annualSavings && (
                        <Typography variant="caption" color="error.dark" sx={{ mt: 1.5, display: 'block' }}>
                          Degradation compounds: Year 1 waste is ${projectionData.projectionYears[0].annualSavings.toLocaleString()}/yr
                          but by Year 5 it grows to ${projectionData.projectionYears[4].annualSavings.toLocaleString()}/yr
                          — and it keeps getting worse.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Alert severity="success" icon={false} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>5-Component Corpus-Validated Model:</strong> Total Annual Value of ${results.totalAnnualValue.toLocaleString()} includes
                  energy savings (${results.valueBreakdown?.energySavings?.toLocaleString() || 0}) plus avoided costs (${results.totalAvoidedCosts.toLocaleString()}).
                  {results.paybackMonths === 'N/A' ? ' Enter utility rate to calculate payback.' : ` True payback: ${results.paybackMonths} months.`}
                  {' '}
                  <Box
                    component="span"
                    sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
                    onClick={() => setModalMetric('total_annual_value')}
                  >
                    See how this is calculated
                  </Box>
                </Typography>
              </Alert>

              {/* Lifetime Projections Section */}
              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}
                  onClick={() => setShowLifetime(!showLifetime)}
                >
                  <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" color="primary.main">
                    Lifetime Savings Projection
                  </Typography>
                  <Chip
                    label="NEW"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                  <IconButton size="small" sx={{ ml: 'auto' }}>
                    {showLifetime ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={showLifetime}>
                  {/* Model Comparison */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ScienceIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2">
                        Degradation Model Comparison (Age: {formData.equipmentAge} years)
                      </Typography>
                      <IconButton size="small" onClick={() => setModalMetric('corpus_vs_ashrae')} sx={{ p: 0, ml: 'auto' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, bgcolor: (theme) => theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Corpus Model (n={AGE_DATA_SAMPLE_SIZE})
                          </Typography>
                          <Typography variant="h5" color="success.main" fontWeight={700}>
                            {modelComparison.corpus.expectedPct}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            From PE-certified measurements
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ASHRAE Model (theoretical)
                          </Typography>
                          <Typography variant="h5" color="text.primary" fontWeight={700}>
                            {modelComparison.ashrae.expectedPct}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Industry standard ~2%/year
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {parseFloat(modelComparison.comparison.difference) > 5 && (
                      <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
                        <Typography variant="caption">
                          Corpus data shows {modelComparison.comparison.difference.toFixed(1)}% higher degradation
                          than ASHRAE theoretical model. Real-world conditions typically exceed theoretical estimates.
                        </Typography>
                      </Alert>
                    )}
                  </Paper>

                  {/* Preventive vs Restorative ROI Comparison */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (theme) => theme.palette.mode === 'light' ? 'primary.50' : 'rgba(25, 118, 210, 0.1)', border: '2px solid', borderColor: 'primary.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CompareArrowsIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Preventive vs Restorative ROI
                      </Typography>
                      <Tooltip title="Preventive ROI calculates value of PREVENTING future degradation. Restorative ROI calculates value of RESTORING existing degradation. Both are shown regardless of equipment age.">
                        <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 'auto' }} />
                      </Tooltip>
                    </Box>

                    <Grid container spacing={2}>
                      {/* Preventive ROI */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          p: 2,
                          bgcolor: (theme) => formData.equipmentAge <= 3
                            ? (theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)')
                            : 'action.hover',
                          borderRadius: 1,
                          border: formData.equipmentAge <= 3 ? '2px solid' : '1px solid',
                          borderColor: formData.equipmentAge <= 3 ? 'success.main' : 'divider',
                          height: '100%'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" color={formData.equipmentAge <= 3 ? 'success.main' : 'text.primary'}>
                              Preventive ROI
                            </Typography>
                            <IconButton size="small" onClick={() => setModalMetric('preventive_roi')} sx={{ p: 0 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 14, color: formData.equipmentAge <= 3 ? 'success.main' : 'text.secondary' }} />
                            </IconButton>
                            {formData.equipmentAge <= 3 && (
                              <Chip label="Best ROI" size="small" color="success" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Avoided future degradation over {roiComparison?.preventive?.equipmentLifeYears || 'N/A'} year life
                          </Typography>
                          <Typography variant="h4" color={formData.equipmentAge <= 3 ? 'success.main' : 'text.primary'} fontWeight={700}>
                            {roiComparison?.preventive?.lifetimeROIPct || 0}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Lifetime Savings: ${(roiComparison?.preventive?.totalPreventedCost || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Payback: {roiComparison?.preventive?.paybackYear ? `Year ${roiComparison.preventive.paybackYear}` : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Restorative ROI */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          p: 2,
                          bgcolor: (theme) => formData.equipmentAge > 3
                            ? (theme.palette.mode === 'light' ? 'warning.50' : 'rgba(237, 108, 2, 0.15)')
                            : 'action.hover',
                          borderRadius: 1,
                          border: formData.equipmentAge > 3 ? '2px solid' : '1px solid',
                          borderColor: formData.equipmentAge > 3 ? 'warning.main' : 'divider',
                          height: '100%'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" color={formData.equipmentAge > 3 ? 'warning.dark' : 'text.primary'}>
                              Restorative ROI
                            </Typography>
                            <IconButton size="small" onClick={() => setModalMetric('restorative_roi')} sx={{ p: 0 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 14, color: formData.equipmentAge > 3 ? 'warning.dark' : 'text.secondary' }} />
                            </IconButton>
                            {formData.equipmentAge >= 10 && (
                              <Chip label="High Immediate Impact" size="small" color="warning" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Restored {roiComparison?.restorative?.degradationRestoredPct || 0}% over {roiComparison?.restorative?.remainingLifeYears || 'N/A'} remaining years
                          </Typography>
                          <Typography variant="h4" color={formData.equipmentAge > 3 ? 'warning.dark' : 'text.primary'} fontWeight={700}>
                            {roiComparison?.restorative?.lifetimeROIPct || 0}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Lifetime Savings: ${(roiComparison?.restorative?.totalLifetimeSavings || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Annual Savings: ${(roiComparison?.restorative?.annualSavings || 0).toLocaleString()}/yr
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Key Insight */}
                    <Alert
                      severity={formData.equipmentAge <= 3 ? 'success' : 'info'}
                      sx={{ mt: 2 }}
                      icon={<TrendingUpIcon />}
                    >
                      <Typography variant="body2">
                        {formData.equipmentAge <= 3 ? (
                          <>
                            <strong>Day 1 Treatment = Best ROI:</strong> Treating new equipment now prevents{' '}
                            {roiComparison?.preventive?.equipmentLifeYears || 'N/A'} years of efficiency loss.
                            Even with only {roiComparison?.restorative?.degradationRestoredPct || 5}% immediate improvement,
                            the preventive value ({roiComparison?.preventive?.lifetimeROIPct || 0}% ROI) is nearly
                            2x higher than waiting until year 10.
                          </>
                        ) : formData.equipmentAge >= 10 ? (
                          <>
                            <strong>Significant Restoration Available:</strong> With {roiComparison?.restorative?.degradationRestoredPct || 0}%
                            existing degradation, this equipment will show dramatic immediate improvement.
                            Combined preventive + restorative value protects the remaining{' '}
                            {roiComparison?.restorative?.remainingLifeYears || 0} years of equipment life.
                          </>
                        ) : (
                          <>
                            <strong>Hybrid Value:</strong> At {formData.equipmentAge} years, this equipment has both
                            existing degradation to restore ({roiComparison?.restorative?.degradationRestoredPct || 0}%)
                            AND significant remaining life to protect. Both ROI calculations apply.
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </Paper>

                  {/* Lifetime Savings Summary */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (theme) => theme.palette.mode === 'light' ? 'secondary.50' : 'rgba(156, 39, 176, 0.1)' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Lifetime Savings Summary
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Remaining Life
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {lifetimeProjection.lifetime.remainingLifeYears} years
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          of {lifetimeProjection.lifetime.expectedLifeYears} yr expected
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total Lifetime Savings
                          </Typography>
                          <IconButton size="small" onClick={() => setModalMetric('lifetime_savings')} sx={{ p: 0 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </IconButton>
                        </Box>
                        <Typography variant="h6" color="success.main" fontWeight={700}>
                          ${lifetimeProjection.lifetime.totalSavings.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          over remaining life
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Lifetime ROI
                          </Typography>
                          <IconButton size="small" onClick={() => setModalMetric('five_year_roi')} sx={{ p: 0 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </IconButton>
                        </Box>
                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                          {lifetimeProjection.roi.lifetimeROIPct}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          return on treatment
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Year-by-Year Projection Table */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2">
                        Year-by-Year Projection
                      </Typography>
                      <Chip
                        label={formData.paymentOption === 'deferred' ? '36 Monthly Payments' : 'Upfront'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>

                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Year</TableCell>
                            <TableCell align="right">Savings</TableCell>
                            <TableCell align="right">Payment</TableCell>
                            <TableCell align="right">Net Cash Flow</TableCell>
                            <TableCell align="right">Cumulative</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectionData.projectionYears.slice(0, 10).map((year) => {
                            // Calculate payment for this year based on payment option
                            const yearPayment = formData.paymentOption === 'deferred' && year.year <= 3
                              ? results.treatmentCost / 3
                              : (year.year === 1 && formData.paymentOption === 'upfront' ? results.treatmentCost : 0)
                            const netCashFlow = year.annualSavings - yearPayment
                            const cumulativePayment = formData.paymentOption === 'deferred'
                              ? Math.min(year.year, 3) * (results.treatmentCost / 3)
                              : results.treatmentCost
                            const cumulativeNet = year.cumulativeSavings - cumulativePayment

                            return (
                              <TableRow
                                key={year.year}
                                sx={{
                                  bgcolor: (theme) => netCashFlow >= 0 && year.year === 1
                                    ? (theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)')
                                    : 'inherit'
                                }}
                              >
                                <TableCell>
                                  Year {year.year}
                                  {cumulativeNet >= 0 && year.year > 1 && projectionData.projectionYears[year.year - 2] &&
                                   (year.cumulativeSavings - (formData.paymentOption === 'deferred'
                                     ? Math.min(year.year - 1, 3) * (results.treatmentCost / 3)
                                     : results.treatmentCost)) < 0 && (
                                    <Chip label="Breakeven" size="small" color="success" sx={{ ml: 1 }} />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  ${year.annualSavings.toLocaleString()}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                  {yearPayment > 0 ? `-$${Math.round(yearPayment).toLocaleString()}` : '—'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ color: netCashFlow >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                                >
                                  ${Math.round(netCashFlow).toLocaleString()}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ color: cumulativeNet >= 0 ? 'success.main' : 'error.main' }}
                                >
                                  ${Math.round(cumulativeNet).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </Box>

                    {projectionData.projectionYears.length > 10 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Showing first 10 of {projectionData.projectionYears.length} years
                      </Typography>
                    )}
                  </Paper>

                  <Alert severity="info" sx={{ mt: 2 }} icon={<ScienceIcon />}>
                    <Typography variant="caption">
                      <strong>How this works:</strong> PermaFrost NMR restores heat exchanger efficiency to
                      near-design capacity. The "degradation %" represents efficiency loss that existed before
                      treatment. Lifetime savings assume consistent annual benefits over remaining equipment life
                      (ASHRAE median: {getExpectedLife(formData.equipmentType)} years for {formData.equipmentType}).
                    </Typography>
                  </Alert>
                </Collapse>
              </Box>

              {calculated && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Tooltip title={savedToCompare ? 'Saved!' : `Add to comparison (${roiScenarios.length}/3 scenarios)`}>
                    <span>
                      <Button
                        variant="outlined"
                        color={savedToCompare ? 'success' : 'secondary'}
                        startIcon={<CompareArrowsIcon />}
                        onClick={handleSaveToCompare}
                        disabled={savedToCompare || roiScenarios.length >= 3}
                      >
                        {savedToCompare ? 'Saved to Compare' : 'Save to Compare'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={modalMetric}
        open={Boolean(modalMetric)}
        onClose={() => setModalMetric(null)}
      />
    </Box>
  )
}

export default ROICalculator
